import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@mlm/ui';
import { reportSalesByBranch } from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Completed-order revenue per branch.
 *
 * The card used to own its own multi-select checkboxes and filter client-side.
 * That row is gone: the dashboard now has ONE page-level branch filter, and two
 * branch controls on a page can only contradict each other. Filtering is now
 * server-side like every other widget, which also means this card and the MTD
 * sales figure above it are narrowed by the same SQL predicate and can't drift.
 */
export function SalesByBranchCard({
  branchId,
  branchLabel,
}: {
  branchId?: string;
  branchLabel?: string | null;
}) {
  const canRead = useHasPerm('report.read');

  const q = useQuery({
    queryKey: ['dashboard', 'sales-by-branch', branchId ?? null],
    queryFn: () => reportSalesByBranch({ branchId }),
    refetchInterval: 45_000,
    enabled: canRead,
    placeholderData: (prev) => prev,
  });

  const rows = q.data ?? [];

  const totals = useMemo(
    () =>
      rows.reduce(
        (a, r) => ({
          orders: a.orders + r.ordersCount,
          gross: a.gross + Number(r.grossTotal),
          pv: a.pv + Number(r.pvTotal),
        }),
        { orders: 0, gross: 0, pv: 0 },
      ),
    [rows],
  );

  // The dashboard route itself isn't permission-guarded, but this endpoint is —
  // stay silent rather than showing a "no access" card among live widgets.
  if (!canRead) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Sales per branch</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        ) : !rows.length ? (
          <p className="p-6 text-sm text-muted-foreground">
            {branchId ? 'No completed sales for this branch.' : 'No completed sales yet.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2">Branch</th>
                  <th className="px-4 py-2 text-right">Orders</th>
                  <th className="px-4 py-2 text-right">Gross</th>
                  <th className="px-4 py-2 text-right">PV</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.branchId ?? 'unassigned'} className="border-b last:border-0">
                    <td className="px-4 py-2">
                      {/* Branch-less rows are real revenue (house/merchant ecom
                          placed against no branch), so they're labelled rather
                          than dropped — dropping them would break the total.
                          They only appear UNFILTERED: a branch filter excludes
                          them everywhere on this page, and this row is where
                          that excluded money stays visible. */}
                      {r.branchName ?? (
                        <span className="text-muted-foreground">Unassigned (online)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.ordersCount}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{peso(r.grossTotal)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {Number(r.pvTotal).toLocaleString('en-PH')}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2">
                <tr className="font-medium">
                  <td className="px-4 py-2">
                    Total
                    {branchId && branchLabel ? (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {branchLabel} only
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{totals.orders}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{peso(totals.gross)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {totals.pv.toLocaleString('en-PH')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
